import PropTypes from 'prop-types';
import React, { Component } from 'react';
import RosettaFormattedMessage from '../../common/rosetta_formatted_message';
import Slider from 'react-slick';
import Product from './product';
import MediaQuery from '../../common/media_query';
import { getBoundingClientRect, throttle } from '../../../utils/dom';
import { getNextArrow, getPrevArrow } from '../../hocs/carousel/arrows';
import { sendAnalyticsEvent, sendDataLayerEvent } from '../../../utils/analytics';
import getProductCarouselWidth from '../../../utils/getProductCarouselWidth';
import classNames from 'classnames';
import {get, map, filter} from 'lodash';
import { injectIntl } from 'react-intl';
import { EDITION_ID_EN_US } from '../../../constants';

export const MAX_WIDTH = 9999999;
export const SLIDE_WIDTH = 290;
export const THROTTLE_WAIT = 500;

const getProductsWithImages = (props) => {
  return filter(get(props, 'content.products'), 'thumbnail_url');
};

/*
 * PrevArrow and NextArrow onClick and className properties are implicitly
 * passed by react-slider.
 */

const getSliderProps = ({ width: carouselWidth, products, intl }) => {
  const [firstProduct] = products;
  const carouselType = 'product';
  const uuid = get(firstProduct, 'id');

  const shouldRenderArrows = products.length > 1;

  const widths = {
    carouselWidth,
    slideWidth: SLIDE_WIDTH
  };

  const arrowTypes = ['carousel-arrow-left', 'carousel-arrow-right'];

  const [DesktopPrevArrow, DesktopNextArrow] = shouldRenderArrows ? [
    getPrevArrow(uuid, arrowTypes[0], intl),
    getNextArrow(uuid, carouselType, { widths }, arrowTypes[1], intl)
  ] : [];

  const [MobilePrevArrow, MobileNextArrow] = shouldRenderArrows ? [
    getPrevArrow(uuid, arrowTypes[0], intl),
    getNextArrow(uuid, carouselType, { widths }, arrowTypes[1], intl)
  ] : [];

  return {

    className: 'products',
    dots: false,
    infinite: false,
    slidesToScroll: 1,
    touchThreshold: 20,
    variableWidth: true,

    beforeChange: (oldIndex, newIndex) => {
      const direction = oldIndex > newIndex ? 'Left' : 'Right';
      sendDataLayerEvent('Click', {
        actionType: `${direction} click`,
        linkType: 'product carousel',
        pageModule: 'Main content'
      });
    },

    afterChange() {
      sendAnalyticsEvent('Shopping', 'Swipe');
    },

    responsive: [

      /*
       * Mobile & Tablet
       */

      {
        breakpoint: 1220,
        settings: {
          arrows: shouldRenderArrows,
          prevArrow: MobilePrevArrow && <MobilePrevArrow />,
          nextArrow: MobileNextArrow && <MobileNextArrow />
        }
      },

      /*
       * Desktop
       */

      {
        breakpoint: MAX_WIDTH,
        settings: {
          arrows: shouldRenderArrows,
          swipe: false,
          swipeToSlide: false,
          touchMove: false,
          draggable: false,
          prevArrow: DesktopPrevArrow && <DesktopPrevArrow />,
          nextArrow: DesktopPrevArrow && <DesktopNextArrow />
        }
      }
    ]
  };
};

export class WrappedProductCarousel extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      leftBound: 0,
      rightBound: 0,
      width: 720
    };

    this.handleResize = throttle(
      this.handleResize.bind(this),
      THROTTLE_WAIT
    );

    this.root = React.createRef();
  }

  componentDidMount() {
    const {
      left: leftBound,
      right: rightBound
    } = getBoundingClientRect(this.root.current);
    this.setState({ leftBound, rightBound });
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    const newCarouselWidth = getProductCarouselWidth(this.props);
    if (newCarouselWidth !== this.state.width) {
      this.setState({ width: newCarouselWidth });
    }
  }

  handleClick = (product_url) => {
    const { adsState } = this.props;
    const editionId = get(this.context, 'edition_data.id', '');
    const fbq = get(window, '_fbq');
    const type = get(adsState, 'ad_data.ad_page_type', '');
    const utmSource = get(this.context, 'location.query.utm_source', '');

    if (editionId === EDITION_ID_EN_US && fbq && type === 'slideshow' && (/^facebook/).test(utmSource)) {
      fbq.push([
        'trackCustom',
        'BuyProduct',
        { type, product_url }
      ]);
    }
  };

  render() {
    const { sold_content_type, hide_product_info_button, intl } = this.props;
    const { width } = this.state;
    const products = getProductsWithImages(this.props);
    const containerClassName = classNames('product-container', {
      'single-product': products.length < 2
    });

    const header = (
      <RosettaFormattedMessage
        parentFilename={__filename}
        id="header"
        description="Text that appears above the product carousel"
        defaultMessage="Shop This"
      />
    );

    return (
      <div className="product-carousel" style={{ width }} ref={this.root}>
        <div className="product-grid-header">
          {header}
        </div>
        <div className={containerClassName}>
          <Slider {...getSliderProps({ width, products, intl })}>
            {
              map(products, (props, dataIndex) => (
                <Product {...props}
                  key={props.id}
                  leftTrackingBoundary={this.state.leftBound}
                  rightTrackingBoundary={this.state.rightBound}
                  sold_content_type={sold_content_type}
                  hide_product_info_button={hide_product_info_button}
                  handleClick={this.handleClick}
                  data_index={dataIndex}
                  hide_product_image
                />
              ))
            }
          </Slider>
        </div>
      </div>
    );
  }
}

WrappedProductCarousel.defaultProps = {
  width: '100%',
  products: []
};

const ProductCarousel = (props) => {
  const products = getProductsWithImages(props);
  if (!products || !products.length) {
    return null;
  }

  const carouselComponent = <WrappedProductCarousel {...props} />;

  return props.product_layout
    ? <MediaQuery breakpoint="mobile">{carouselComponent}</MediaQuery>
    : carouselComponent;
};

const EntryWithProductsContentType = PropTypes.shape({
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      thumbnail_url: PropTypes.string
    }).isRequired
  ).isRequired
});

ProductCarousel.propTypes = {
  adsState: PropTypes.object.isRequired,
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    EntryWithProductsContentType
  ]).isRequired,
  product_layout: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired
};

WrappedProductCarousel.propTypes = {
  adsState: PropTypes.object.isRequired,
  content: EntryWithProductsContentType.isRequired,
  sold_content_type: PropTypes.string,
  hide_product_info_button: PropTypes.bool,
  intl: PropTypes.object.isRequired
};

WrappedProductCarousel.contextTypes = {
  edition_data: PropTypes.object,
  location: PropTypes.object
};

export default injectIntl(ProductCarousel);
